import React, { useEffect, useState } from 'react';
import { api } from '../App';
import CriticalStagesBanner from '../components/CriticalStagesBanner';

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
}

// Normalize human-readable category/shade values to Title Case so the
// Shade Breakdown table renders consistently no matter how the ERP stored
// them (some rows are UPPERCASE like "BOTTOM WEAR", others Proper Case like
// "Black" — user wants one consistent style).
// Skip codes — alphanumeric blobs without spaces (e.g. "PXTFPSSH020468")
// stay as-is so they remain recognizable.
function titleCase(s) {
  if (!s) return s;
  const str = String(s);
  // Looks like a part-code: no spaces AND mostly digits/uppercase → leave alone
  if (!/\s/.test(str) && /[0-9]/.test(str) && str.length > 6) return str;
  return str.toLowerCase().replace(/\b[a-z]/g, c => c.toUpperCase());
}

function todayDDMMYYYY() {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

// Auto-format as user types: "25112025" → "25/11/2025", drops non-digits, max 8 digits
function formatDateInput(value) {
  const digits = (value || '').replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

// Convert any date value (ISO/Date/string) → DD/MM/YYYY
function dateToDDMMYYYYFromAny(dateVal) {
  if (!dateVal) return '';
  const d = new Date(dateVal);
  if (isNaN(d)) return '';
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

// Today as YYYY-MM-DD (for native <input type="date">)
function todayISOforPicker() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Convert any date value → YYYY-MM-DD
function toISOforPicker(dateVal) {
  if (!dateVal) return '';
  const d = new Date(dateVal);
  if (isNaN(d)) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Status updater — small dropdown + save
function StatusUpdater({ order }) {
  const OPTIONS = [
    { id: 1, label: 'Pending' },
    { id: 2, label: 'Ok' },
    { id: 3, label: 'Cancel' },
    { id: 4, label: 'Suspend' },
    { id: 5, label: 'Excess' },
    { id: 6, label: 'Close' },
    { id: 8, label: 'Dropped' },
    { id: 9, label: 'Hold By Client' }
  ];
  const current = OPTIONS.find(o => o.label === (order.Status || 'Pending')) || OPTIONS[0];
  const [selectedId, setSelectedId] = useState(current.id);
  const [saving, setSaving] = useState(false);
  const [savedLabel, setSavedLabel] = useState(null);

  async function save() {
    if (selectedId === current.id && !savedLabel) return;
    setSaving(true);
    try {
      const res = await api(`/orders/${encodeURIComponent(order.tas_no)}/status`, {
        method: 'POST',
        body: JSON.stringify({ statusid: selectedId })
      });
      if (res.success) {
        const lbl = OPTIONS.find(o => o.id === selectedId)?.label;
        setSavedLabel(lbl);
      }
    } finally {
      setSaving(false);
    }
  }

  const dirty = selectedId !== current.id;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ fontSize: 10, color: 'var(--text3)', letterSpacing: 1 }}>STATUS</span>
      <select
        value={selectedId}
        onChange={e => setSelectedId(parseInt(e.target.value))}
        disabled={saving}
        style={{
          background: 'var(--border)', border: '1px solid var(--border)', borderRadius: 4,
          color: 'var(--text)', fontSize: 12, padding: '5px 8px', outline: 'none', cursor: 'pointer'
        }}
      >
        {OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
      {dirty && (
        <button
          onClick={save}
          disabled={saving}
          style={{
            background: 'var(--gold)', color: '#FFFFFF', border: 'none', borderRadius: 4,
            padding: '5px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer', letterSpacing: 1
          }}
        >{saving ? 'SAVING…' : 'SAVE'}</button>
      )}
      {!dirty && savedLabel && (
        <span style={{ fontSize: 10, color: 'var(--green)' }}>✓ saved</span>
      )}
    </div>
  );
}

export default function OrderDetail({ order, onBack, onXP }) {
  const [stages, setStages] = useState([]);
  const [shades, setShades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null); // selected stage for update
  const [actualDate, setActualDate] = useState(todayISOforPicker()); // YYYY-MM-DD for native picker
  const [remark, setRemark] = useState('');
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');
  const [showShades, setShowShades] = useState(true);
  const [showCompleted, setShowCompleted] = useState(false); // collapsed by default
  const [openCategories, setOpenCategories] = useState({}); // track expanded categories within pending
  const [closeConfirm, setCloseConfirm] = useState(false); // status-change modal
  const [closing, setClosing] = useState(false);
  const [orderStatusOverride, setOrderStatusOverride] = useState(null); // local override after change
  const [showOtherStatuses, setShowOtherStatuses] = useState(false);
  // Order Closure Date editor
  const [editingClosure, setEditingClosure] = useState(false);
  const [closureDate, setClosureDate] = useState('');
  const [savingClosure, setSavingClosure] = useState(false);
  const [closureOverride, setClosureOverride] = useState(null);
  // Production + Dispatch tiles (same as the All-TAS tab drill)
  const [expTile, setExpTile] = useState(null);   // 'production' | 'dispatch' | null
  const [prod, setProd] = useState(null);         // tas-production shade rows

  async function saveClosureDate() {
    if (!closureDate) return;
    setSavingClosure(true);
    try {
      const res = await api(`/orders/${encodeURIComponent(order.tas_no)}/closure-date`, {
        method: 'POST',
        body: JSON.stringify({ date: closureDate })
      });
      if (res.success) {
        setClosureOverride(closureDate);
        setEditingClosure(false);
      }
    } finally { setSavingClosure(false); }
  }

  // All ERP status options. id 6 (Close) is the primary one for partial dispatch.
  const STATUS_OPTIONS = [
    { id: 6, label: 'Close',   desc: 'Order finalised — remaining qty will not ship', color: 'var(--gold)', primary: true },
    { id: 3, label: 'Cancel',  desc: 'Buyer cancelled the order',           color: 'var(--red)' },
    { id: 4, label: 'Suspend', desc: 'On hold temporarily — can resume',     color: 'var(--yellow)' },
    { id: 9, label: 'Hold By Client', desc: 'On hold at buyer/client request', color: 'var(--yellow)' },
    { id: 5, label: 'Excess',  desc: 'Shipped more than ordered',            color: 'var(--teal)' },
    { id: 8, label: 'Dropped', desc: 'Internal drop — will not pursue',     color: 'var(--red)' },
    { id: 2, label: 'Ok',      desc: 'Mark as completed/accepted',           color: 'var(--green)' },
    { id: 1, label: 'Pending', desc: 'Reopen — back to pending',             color: 'var(--text3)' },
  ];

  async function applyStatus(statusId, label) {
    setClosing(true);
    try {
      const res = await api(`/orders/${encodeURIComponent(order.tas_no)}/status`, {
        method: 'POST',
        body: JSON.stringify({ statusid: statusId })
      });
      if (res.success) {
        setOrderStatusOverride(label);
        setCloseConfirm(false);
        setShowOtherStatuses(false);
        // Status changed → return user to Order in Hand list (refreshed)
        if (typeof onBack === 'function') onBack();
      }
    } finally { setClosing(false); }
  }

  useEffect(() => {
    Promise.all([
      api(`/orders/${encodeURIComponent(order.tas_no)}/stages`),
      api(`/orders/${encodeURIComponent(order.tas_no)}/shades`),
      api(`/orders/tas-production/${encodeURIComponent(order.tas_no)}`).catch(() => [])
    ]).then(([s, sh, pr]) => {
      // Hide "Estimated Tna" — keep all Routing Stage categories so users can update actual dates
      const filteredStages = (Array.isArray(s) ? s : []).filter(x =>
        !(x.stage_category || '').toLowerCase().includes('estimated tna')
      );
      setStages(filteredStages);
      setShades(Array.isArray(sh) ? sh : []);
      setProd(Array.isArray(pr) ? pr : []);
      setLoading(false);
    });
  }, [order.tas_no]);

  async function handleSkip() {
    if (!updating) return;
    if (!window.confirm(`Mark "${updating.stage_name}" as NOT APPLICABLE? This will skip it.`)) return;
    setSaving(true);
    try {
      const res = await api('/stages/skip', {
        method: 'POST',
        body: JSON.stringify({ uniqueid: updating.uniqueid, source: order.order_type || 'garment' })
      });
      if (res.success) {
        onXP(`✓ ${updating.stage_name} skipped`);
        // Remove from local state (filtered list hides non-pending automatically, but applicable=0 is its own thing)
        setStages(prev => prev.map(s =>
          s.uniqueid === updating.uniqueid
            ? { ...s, applicable: 0, status: 'done' } // mark done so it disappears from pending view
            : s
        ));
        setUpdating(null);
        setRemark('');
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdate() {
    if (!updating || !actualDate) return;
    // State holds YYYY-MM-DD from the native date picker; convert to DD/MM/YYYY for the API
    const [yyyy, mm, dd] = actualDate.split('-');
    const ddmmyyyy = `${dd}/${mm}/${yyyy}`;
    setSaving(true);
    try {
      const res = await api('/stages/update', {
        method: 'POST',
        body: JSON.stringify({ uniqueid: updating.uniqueid, actual_date: ddmmyyyy, remark, source: order.order_type || 'garment' })
      });
      if (res.success) {
        onXP(`+${res.xp_earned} XP · ${res.message}`);
        const parsed = new Date(actualDate + 'T00:00:00').toISOString();
        setStages(prev => prev.map(s =>
          s.uniqueid === updating.uniqueid
            ? { ...s, actual_date: parsed, status: 'done' }
            : s
        ));
        setUpdating(null);
        setRemark('');
      }
    } finally {
      setSaving(false);
    }
  }

  // Business rule: if ANY qty has been dispatched (header total OR any shade row),
  // all stages are considered "completed" — ERP stops tracking stages once production
  // physically ships. Shows up as "Not Applicable" / hides pending stages.
  // Sum from shades because order.dsp_qty isn't always populated in /orders payload.
  const shadesDspTotal = shades.reduce((a, s) => a + (s.dspqty || 0), 0);
  const orderDspTotal  = order.dsp_qty || order.DspQty || 0;
  const totalDispatched = Math.max(shadesDspTotal, orderDspTotal);
  const hasAnyDispatch = totalDispatched > 0;
  const totalOrdered = shades.reduce((a, s) => a + (s.orderqty || 0), 0) || (order.orderqty || 0);
  const isFullyDispatched = hasAnyDispatch && totalOrdered > 0 && totalDispatched >= totalOrdered;

  // PHANTOM stages = rows the ERP inserted but that aren't part of this order's plan.
  // Two variants:
  //   (a) GSP/10020 legacy: days=0, plan+actual both null, applicable=0 on every row.
  //   (b) GSP/010570 variant: days=0, plan=placeholder date (e.g. 28 May 26), actual=null,
  //       applicable=1. Some ERP flow filled a placeholder plan_dt on the 4 unused stages,
  //       so `!planned_date` alone wasn't enough.
  // `days === 0` is the reliable phantom marker — real used fabric stages always have
  // days > 0. Guard with "some other stage has actual_dt" so we don't treat a fresh
  // never-touched order as all-phantom.
  const hasActualDate = s => s.actual_date && new Date(s.actual_date) > new Date('1900-01-02');
  const hasPlannedDate = s => s.planned_date && new Date(s.planned_date) > new Date('1900-01-02');
  const applOk = s => !(s.applicable === 0 || s.applicable === '0' || s.applicable === false);
  // A stage is shown when it's done, OR it has a PLANNED DATE **and** is applicable.
  // So PCD appears once it's both scheduled and marked applicable; unused template
  // rows (applicable=0, even with a placeholder plan date) stay hidden.
  const isRelevant = s => hasActualDate(s) || (hasPlannedDate(s) && applOk(s));
  const realStages = stages.filter(isRelevant);
  // If every real stage has an actual date, the order is effectively delivered even if
  // it has no dispatch record (fabric orders don't have dispatch qty).
  const allRealDone = realStages.length > 0 && realStages.every(s =>
    s.status === 'done' || (s.actual_date && new Date(s.actual_date) > new Date('1900-01-02'))
  );

  const done = (hasAnyDispatch || allRealDone) ? realStages.length : realStages.filter(s => s.status === 'done').length;
  const total = realStages.length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  // Sort stages by sort_order to find current/expected
  const orderedStages = [...stages].sort((a, b) => {
    const sa = (a.sort_order || '').toString().split('_').map(Number);
    const sb = (b.sort_order || '').toString().split('_').map(Number);
    return (sa[0] - sb[0]) || (sa[1] - sb[1]);
  });

  // sortKey + auto-skip helpers (defined first so currentStage can use them)
  const sortKey = s => {
    const p = (s?.sort_order || '').toString().split('_').map(Number);
    return (p[0] || 0) * 1000 + (p[1] || 0);
  };
  // AUTO-SKIP RULE: If a later stage (higher sort_order) is marked done OR has an
  //   actual_date entered, every earlier stage that hasn't been touched is implicitly
  //   skipped. Those stages no longer count as "current" / "pending".
  const maxDoneKey = stages
    .filter(s => s.status === 'done' || hasActualDate(s))
    .reduce((mx, s) => Math.max(mx, sortKey(s)), -1);
  const isAutoSkipped = s =>
    s.status !== 'done' && !hasActualDate(s) && sortKey(s) < maxDoneKey;

  // CURRENT = stage whose name matches order.current_pending_stage (which now
  // comes from vw_pendingformMerchandisers_actual / vw_pendingformgsp_actual,
  // the canonical ERP source — already filters out applicable=0). Fall back to
  // the first non-done, non-auto-skipped stage if the view doesn't return one
  // or its name doesn't match any row in /stages.
  const _viewStageName = (order?.current_pending_stage || '').trim().toLowerCase();
  const currentStage = allRealDone ? null : (
    (_viewStageName && orderedStages.find(s => (s.stage_name || '').trim().toLowerCase() === _viewStageName))
    || orderedStages.find(s => s.status !== 'done' && !isAutoSkipped(s) && isRelevant(s))
  );

  // EXPECTED = latest PENDING stage whose planned_date has already passed.
  // Earlier we considered all stages (done + pending) — for an order like
  // GSP/010568 where DYEING (done) had a planned date in the past but every
  // pending stage was still in the future, that picked DYEING as expected and
  // wrongly flagged the order DELAYED. Now we only count pending stages, so an
  // order is "behind" only when something that should've been done by now isn't.
  const today = new Date();
  const expectedStage = allRealDone ? null : [...orderedStages].reverse().find(s =>
    s.status !== 'done' &&
    !isAutoSkipped(s) &&
    isRelevant(s) &&
    s.planned_date && new Date(s.planned_date) <= today
  );

  // Also treat as delayed when the order's own DELIVERY date is in the past
  // (and nothing has been dispatched). Without this, an order can be 100+ days
  // past delivery but still show "ON TRACK" just because no pending stage's
  // planned_date is recorded as passed. Suppressed once every real stage is done
  // (all fabric received / all garment stages closed) — the order is delivered
  // even if its original delivery date was in the past.
  const orderDelivery = order?.delivery_dt ? new Date(order.delivery_dt) : null;
  const isDeliveryPast = !allRealDone && !!orderDelivery && orderDelivery <= today;

  // Also delayed when the ERP's CURRENT pending stage is itself past its plan date
  // and not done. This aligns the banner with the ERP view (vw_pending…_actual /
  // the alerts) — without it, an out-of-order completion (e.g. a later FAB ETA
  // dated while ORDER SHARING is still open) auto-skips the real current stage and
  // wrongly shows ON TRACK even though that stage is weeks past due.
  const currentPastDue = !allRealDone && currentStage &&
    currentStage.status !== 'done' &&
    currentStage.planned_date && new Date(currentStage.planned_date) <= today;

  const isDelayed = !!expectedStage || isDeliveryPast || !!currentPastDue;
  const stagesBehind = isDelayed
    ? Math.max(currentPastDue ? 1 : 0, orderedStages.filter(s =>
        s.status !== 'done' && !isAutoSkipped(s) && isRelevant(s) &&
        s.planned_date && new Date(s.planned_date) <= today
      ).length)
    : 0;

  // Bucket logic (downstream use):
  //   PENDING   = not done AND still applicable AND not auto-skipped
  //   COMPLETED = done AND still applicable
  //   SKIPPED   = applicable === 0  OR  auto-skipped

  const isApplicable = s => hasAnyDispatch
    ? false
    : (isRelevant(s) && !isAutoSkipped(s));

  // PENDING STAGES section removed per request (Order-in-Hand detail) — forced empty.
  const filtered = true
    ? []
    : stages
        .filter(s => s.status !== 'done' && isApplicable(s))
        .sort((a, b) => sortKey(a) - sortKey(b));

  const stageIcon = (s) => {
    if (s.status === 'done') return { icon: '✓', cls: 'done' };
    if (s.status === 'overdue') return { icon: '!', cls: 'overdue' };
    if (s.status === 'due_soon') return { icon: '~', cls: 'due_soon' };
    return { icon: '○', cls: 'pending' };
  };

  return (
    <div className="screen">
      {/* Header */}
      <div className="header">
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'var(--gold)', fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
        <div style={{ flex: 1, marginLeft: 12 }}>
          <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, fontWeight: 300 }}>{order.tas_no}</div>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>{titleCase(order.buyer)}</div>
        </div>
        {/* Order Dt — for TAS uses enquiry_date (initial booking), for GSP uses gsp_date */}
        {(() => {
          const orderDt = order.order_type === 'fabric' ? order.gsp_date : order.enquiry_date;
          return orderDt ? (
            <div style={{ textAlign: 'right', marginRight: 16 }}>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>Order Dt</div>
              <div style={{ fontSize: 13, color: 'var(--text2)' }}>{fmtDate(orderDt)}</div>
            </div>
          ) : null;
        })()}
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>Delivery</div>
          <div style={{ fontSize: 13, color: order.Delay_days < 0 ? 'var(--red)' : 'var(--teal)' }}>
            {fmtDate(order.delivery_dt)}
          </div>
        </div>
      </div>

      {/* Progress */}
      <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: 'var(--text3)' }}>{done} of {total} stages complete</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {hasAnyDispatch ? (
              <span title={`${totalDispatched.toLocaleString('en-IN')} of ${totalOrdered.toLocaleString('en-IN')} pcs dispatched`}
                style={{
                fontSize: 11, padding: '4px 10px', borderRadius: 12, letterSpacing: 1, fontWeight: 600,
                background: isFullyDispatched ? 'var(--green-bg)' : 'var(--gold-bg)',
                color: isFullyDispatched ? 'var(--green)' : 'var(--gold)'
              }}>
                {isFullyDispatched ? '✓ FULLY DISPATCHED'
                  : `🚚 PARTIAL DISPATCH${totalOrdered > 0 ? ` · ${(() => { const raw = totalDispatched * 100 / totalOrdered; return totalDispatched >= totalOrdered ? '100' : raw > 99 ? (Math.floor(raw * 100) / 100).toFixed(2) : Math.round(raw); })()}%` : ''}`}
                {!isFullyDispatched && totalOrdered > 0 && (
                  <span style={{ fontWeight: 400, letterSpacing: 0, marginLeft: 6, color: 'var(--text2)' }}>
                    ({totalDispatched.toLocaleString('en-IN')}/{totalOrdered.toLocaleString('en-IN')} pcs)
                  </span>
                )}
              </span>
            ) : (
              <StatusUpdater order={order} />
            )}
            <span style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 20, color: 'var(--gold)' }}>{pct}%</span>
          </div>
        </div>
        <div style={{ height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ height: '100%', background: 'var(--gold)', borderRadius: 3, width: `${pct}%`, transition: 'width 0.5s' }} />
        </div>
        <div style={{ display: 'flex', gap: 12, marginTop: 10, fontSize: 11, color: 'var(--text3)', flexWrap: 'wrap', alignItems: 'center' }}>
          <span>📦 {((order.bal_qty || order.orderqty || 0)).toLocaleString()} {order.order_type === 'fabric' ? 'mtrs' : 'pcs'}</span>
          <span>🏭 {titleCase(order.factory) || '—'}</span>
          <span>👤 {titleCase(order.merchandiser) || '—'}</span>
          <span style={{ color: order.Delay_days < 0 ? 'var(--red)' : 'var(--teal)' }}>
            🚚 Delivery: {order.delivery_dt ? fmtDate(order.delivery_dt) : '—'}
            {order.Delay_days != null && order.Delay_days < 0 && (
              <span style={{ marginLeft: 4, opacity: 0.85 }}>· {Math.abs(order.Delay_days)}d late</span>
            )}
          </span>
          {(order.order_type !== 'fabric') && (() => {
            const cur = closureOverride || order.order_closure_dt;
            return editingClosure ? (
              <span style={{ display: 'inline-flex', gap: 5, alignItems: 'center' }}>
                <span style={{ color: 'var(--gold)' }}>📅 Closure:</span>
                <input
                  type="date" value={closureDate}
                  onChange={e => setClosureDate(e.target.value)}
                  disabled={savingClosure}
                  style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 3, color: 'var(--text)', fontSize: 11, padding: '2px 5px', outline: 'none' }}
                />
                <button onClick={saveClosureDate} disabled={savingClosure || !closureDate}
                  style={{ background: 'var(--gold)', color: '#FFFFFF', border: 'none', borderRadius: 3, padding: '2px 8px', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>
                  {savingClosure ? '…' : 'SAVE'}
                </button>
                <button onClick={() => setEditingClosure(false)} disabled={savingClosure}
                  style={{ background: 'transparent', color: 'var(--text3)', border: 'none', fontSize: 11, cursor: 'pointer', padding: '2px 4px' }}>✕</button>
              </span>
            ) : (
              <span
                onClick={() => {
                  setClosureDate(cur ? new Date(cur).toISOString().slice(0, 10) : todayISOforPicker());
                  setEditingClosure(true);
                }}
                style={{ cursor: 'pointer', color: 'var(--gold)', textDecoration: 'underline', textDecorationColor: '#3a3122', textDecorationStyle: 'dotted' }}
                title="Click to update Order Closure Date"
              >
                📅 Closure: {cur ? fmtDate(cur) : 'Set date →'}
              </span>
            );
          })()}
          {order.order_value > 0 && <span style={{ color: 'var(--gold)', marginLeft: 'auto' }}>₹{(order.order_value/100000).toFixed(1)}L</span>}
        </div>
      </div>

      {/* Production + Dispatch tiles (same as the All-TAS tab drill) */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
        {(() => {
          const pSum = (prod || []).reduce((a, r) => ({
            cut: a.cut + (r.cut || 0), input: a.input + (r.input || 0),
            output: a.output + (r.output || 0), ready: a.ready + (r.ready || 0)
          }), { cut: 0, input: 0, output: 0, ready: 0 });
          const dispShades = (shades || []).filter(s => (s.dspqty || 0) > 0);
          const tiles = [
            { key: 'production', lb: 'PRODUCTION', val: pSum.output, cc: 'var(--teal)' },
            { key: 'dispatch',   lb: 'DISPATCHED', val: totalDispatched, cc: 'var(--green)' },
          ];
          return (
          <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
            {tiles.map(t => (
              <div key={t.key} onClick={() => setExpTile(p => p === t.key ? null : t.key)}
                style={{ background: 'var(--bg2)', border: `1px solid ${expTile === t.key ? t.cc : 'var(--border)'}`, borderRadius: 12, padding: '18px 10px', textAlign: 'center', cursor: 'pointer' }}>
                <div style={{ fontSize: 26, fontWeight: 700, color: t.cc, fontFamily: 'Inter, sans-serif', lineHeight: 1.25 }}>{(t.val || 0).toLocaleString('en-IN')}</div>
                <div style={{ fontSize: 10, color: 'var(--text3)', letterSpacing: 1.2, marginTop: 5 }}>{t.lb}{expTile === t.key ? ' ▲' : ' ▼'}</div>
              </div>
            ))}
          </div>

          {expTile === 'production' && (
            <div style={{ marginTop: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', gap: 8, marginBottom: 10 }}>
                {[['CUTTING', pSum.cut, 'var(--teal)'], ['INPUT', pSum.input, 'var(--gold)'], ['OUTPUT', pSum.output, 'var(--teal)'], ['READY', pSum.ready, 'var(--green)']].map(([lb, val, cc]) => (
                  <div key={lb} style={{ background: 'var(--bg3)', borderRadius: 8, padding: '10px 6px', textAlign: 'center' }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: cc, fontFamily: 'Inter, sans-serif' }}>{(val || 0).toLocaleString('en-IN')}</div>
                    <div style={{ fontSize: 8.5, color: 'var(--text3)', letterSpacing: 1, marginTop: 2 }}>{lb}</div>
                  </div>
                ))}
              </div>
              <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflowX: 'auto' }}>
                {prod === null ? <div style={{ padding: 16, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>Loading…</div>
                  : prod.length === 0 ? <div style={{ padding: 16, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>No production entries</div> : (
                  <table style={{ width: '100%', minWidth: 460, borderCollapse: 'collapse', fontSize: 12, whiteSpace: 'nowrap' }}>
                    <thead><tr>{['DESIGN', 'SHADE', 'CUT', 'INPUT', 'OUTPUT', 'READY'].map((h, i) => (<th key={h} style={{ textAlign: i >= 2 ? 'right' : 'left', padding: '8px 12px', background: 'var(--bg2)', color: 'var(--text3)', fontSize: 10, letterSpacing: 0.6, borderBottom: '1px solid var(--border)' }}>{h}</th>))}</tr></thead>
                    <tbody>
                      {prod.map((l, i) => (
                        <tr key={i}>
                          <td style={{ padding: '7px 12px', color: 'var(--text)', borderBottom: '1px solid var(--border)' }}>{l.design || '—'}</td>
                          <td style={{ padding: '7px 12px', color: 'var(--text)', borderBottom: '1px solid var(--border)' }}>{l.shade || '—'}</td>
                          <td style={{ padding: '7px 12px', textAlign: 'right', color: 'var(--text)', borderBottom: '1px solid var(--border)' }}>{(l.cut || 0).toLocaleString('en-IN')}</td>
                          <td style={{ padding: '7px 12px', textAlign: 'right', color: 'var(--text)', borderBottom: '1px solid var(--border)' }}>{(l.input || 0).toLocaleString('en-IN')}</td>
                          <td style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 700, color: 'var(--teal)', borderBottom: '1px solid var(--border)' }}>{(l.output || 0).toLocaleString('en-IN')}</td>
                          <td style={{ padding: '7px 12px', textAlign: 'right', color: 'var(--green)', borderBottom: '1px solid var(--border)' }}>{(l.ready || 0).toLocaleString('en-IN')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {expTile === 'dispatch' && (
            <div style={{ marginTop: 12 }}>
              <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflowX: 'auto' }}>
                {dispShades.length === 0 ? <div style={{ padding: 16, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>Nothing dispatched yet</div> : (
                  <table style={{ width: '100%', minWidth: 460, borderCollapse: 'collapse', fontSize: 12, whiteSpace: 'nowrap' }}>
                    <thead><tr>{['STYLE', 'SHADE', 'ORDER', 'DISPATCHED', 'BAL', 'RATE', 'AMOUNT'].map((h, i) => (<th key={h} style={{ textAlign: i >= 2 ? 'right' : 'left', padding: '8px 12px', background: 'var(--bg2)', color: 'var(--text3)', fontSize: 10, letterSpacing: 0.6, borderBottom: '1px solid var(--border)' }}>{h}</th>))}</tr></thead>
                    <tbody>
                      {dispShades.map((l, i) => (
                        <tr key={i}>
                          <td style={{ padding: '7px 12px', color: 'var(--text)', borderBottom: '1px solid var(--border)' }}>{l.style_name || '—'}</td>
                          <td style={{ padding: '7px 12px', fontWeight: 600, color: 'var(--text)', borderBottom: '1px solid var(--border)' }}>{l.shade || '—'}</td>
                          <td style={{ padding: '7px 12px', textAlign: 'right', color: 'var(--text)', borderBottom: '1px solid var(--border)' }}>{(l.orderqty || 0).toLocaleString('en-IN')}</td>
                          <td style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 700, color: 'var(--green)', borderBottom: '1px solid var(--border)' }}>{(l.dspqty || 0).toLocaleString('en-IN')}</td>
                          <td style={{ padding: '7px 12px', textAlign: 'right', color: 'var(--text)', borderBottom: '1px solid var(--border)' }}>{(l.bal_qty || 0).toLocaleString('en-IN')}</td>
                          <td style={{ padding: '7px 12px', textAlign: 'right', color: 'var(--text)', borderBottom: '1px solid var(--border)' }}>{l.rate ? '₹' + l.rate : '—'}</td>
                          <td style={{ padding: '7px 12px', textAlign: 'right', color: 'var(--gold)', borderBottom: '1px solid var(--border)' }}>{l.amount ? '₹' + ((l.amount || 0) / 100000).toFixed(2) + 'L' : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
          </>
          );
        })()}
      </div>

      {/* Dispatched banner + close-order CTA */}
      {hasAnyDispatch && (() => {
        const dspQty = totalDispatched;
        const orderedQty = totalOrdered || (order.orderqty || 0);
        const balQty = Math.max(0, orderedQty - dspQty);
        const isClosed = (orderStatusOverride || order.Status) === 'Close';
        const isFullyDelivered = balQty <= 0;
        const headline = isClosed
          ? '✓ ORDER CLOSED'
          : isFullyDelivered ? '✓ FULLY DELIVERED' : '⚠ PARTIALLY DELIVERED';
        const headColor = isClosed ? 'var(--green)' : isFullyDelivered ? 'var(--green)' : 'var(--yellow)';
        const bgColor   = isClosed ? 'var(--green-bg)' : isFullyDelivered ? 'var(--green-bg)' : 'var(--yellow-bg)';
        return (
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', background: bgColor }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 11, color: headColor, letterSpacing: 2, fontWeight: 600 }}>{headline}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                  {dspQty.toLocaleString()} of {orderedQty.toLocaleString()} pcs shipped
                  {balQty > 0 && <span style={{ color: 'var(--red)', marginLeft: 6 }}>· {balQty.toLocaleString()} pcs pending</span>}
                </div>
              </div>
              {!isClosed && balQty > 0 && (
                <button
                  onClick={() => setCloseConfirm(true)}
                  style={{
                    background: 'var(--bg)', color: headColor, border: `1px solid ${headColor}`,
                    borderRadius: 8, padding: '8px 14px', fontSize: 11, fontWeight: 600,
                    cursor: 'pointer', letterSpacing: 1, display: 'inline-flex',
                    alignItems: 'center', gap: 6, transition: 'all 0.15s'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = headColor; e.currentTarget.style.color = '#FFFFFF'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg)'; e.currentTarget.style.color = headColor; }}
                >
                  Close this order →
                </button>
              )}
              {isClosed && (
                <span style={{
                  fontSize: 11, padding: '6px 12px', borderRadius: 12, letterSpacing: 1, fontWeight: 600,
                  background: 'var(--green-bg)', color: 'var(--green)'
                }}>
                  ✓ CLOSED
                </span>
              )}
            </div>
          </div>
        );
      })()}

      {/* Status-change modal — primary action Close + all other options */}
      {closeConfirm && (
        <div className="modal-overlay" onClick={() => !closing && setCloseConfirm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480, margin: '0 auto', borderRadius: 14 }}>
            <div className="modal-title">Change Order Status</div>
            <div className="modal-sub">
              <strong style={{ color: 'var(--gold)' }}>{order.tas_no}</strong>
              {totalDispatched > 0 && (
                <> — <span style={{ color: 'var(--text3)' }}>
                  {totalDispatched.toLocaleString()} of {(totalOrdered || order.orderqty || 0).toLocaleString()} pcs shipped
                </span></>
              )}
            </div>

            {/* Primary CTA: Close (most common for partial dispatch) */}
            {!showOtherStatuses && (
              <>
                <button
                  onClick={() => applyStatus(6, 'Close')}
                  disabled={closing}
                  className="btn-primary"
                  style={{ background: 'var(--gold)', marginBottom: 8 }}
                >
                  {closing ? 'SAVING…' : '✕ CLOSE THIS ORDER'}
                </button>
                <div style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'center', marginBottom: 14, marginTop: -4 }}>
                  Remaining qty won't ship · can reopen later
                </div>
                <button
                  onClick={() => setShowOtherStatuses(true)}
                  className="btn-cancel"
                  style={{ color: 'var(--gold)' }}
                >
                  Other status options ↓
                </button>
                <button onClick={() => setCloseConfirm(false)} className="btn-cancel">Cancel</button>
              </>
            )}

            {/* All status options as cards */}
            {showOtherStatuses && (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                  {STATUS_OPTIONS.map(opt => (
                    <button
                      key={opt.id}
                      onClick={() => applyStatus(opt.id, opt.label)}
                      disabled={closing}
                      style={{
                        background: 'transparent',
                        border: `1px solid ${opt.color}`,
                        borderLeft: `4px solid ${opt.color}`,
                        borderRadius: 6, padding: '12px 14px',
                        textAlign: 'left', cursor: 'pointer',
                        display: 'flex', flexDirection: 'column', gap: 3,
                        opacity: closing ? 0.5 : 1
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = `${opt.color}15`}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <div style={{ fontSize: 13, color: opt.color, fontWeight: 700, letterSpacing: 0.5 }}>{opt.label.toUpperCase()}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)' }}>{opt.desc}</div>
                    </button>
                  ))}
                </div>
                <button onClick={() => setShowOtherStatuses(false)} className="btn-cancel">← Back</button>
              </>
            )}
          </div>
        </div>
      )}

      {/* STAGE STATUS banner removed per request (Order-in-Hand detail) */}
      {false && !hasAnyDispatch && currentStage && (
        <div style={{
          padding: '12px 16px', borderBottom: '1px solid var(--border)',
          background: isDelayed ? 'var(--red-bg)' : 'var(--green-bg)',
          display: 'flex', flexDirection: 'column', gap: 10
        }}>
          {/* Status pill — full width on top */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <div style={{ fontSize: 10, color: 'var(--text3)', letterSpacing: 1 }}>STAGE STATUS</div>
            {isDelayed ? (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: 'var(--red-bg)', color: 'var(--red)',
                padding: '4px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, letterSpacing: 0.5
              }}>
                ⚠ DELAYED · {
                  stagesBehind > 0
                    ? `${stagesBehind} stage${stagesBehind === 1 ? '' : 's'} behind`
                    : 'past delivery date'
                }
              </div>
            ) : (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: 'var(--green-bg)', color: 'var(--green)',
                padding: '4px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, letterSpacing: 0.5
              }}>
                ✓ ON TRACK
              </div>
            )}
          </div>

          {/* Stage cards — grid: 2 cols ≥460px, 1 col on phone. Clickable → opens update modal */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
            <div
              onClick={() => {
                setUpdating(currentStage);
                setActualDate(currentStage.actual_date ? toISOforPicker(currentStage.actual_date) : todayISOforPicker());
                setRemark('');
              }}
              style={{
                padding: 10, borderRadius: 6,
                background: 'rgba(255,255,255,0.03)',
                borderLeft: `3px solid ${isDelayed ? 'var(--red)' : 'var(--green)'}`,
                cursor: 'pointer', transition: 'background 0.15s'
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.07)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
            >
              <div style={{ fontSize: 9, color: 'var(--text3)', letterSpacing: 1, marginBottom: 4 }}>CURRENT STAGE</div>
              <div style={{
                fontSize: 14, color: isDelayed ? 'var(--red)' : 'var(--green)', fontWeight: 600,
                wordBreak: 'break-word', lineHeight: 1.2
              }}>
                {currentStage.stage_name}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                <div style={{ fontSize: 10, color: 'var(--text3)' }}>Plan: {fmtDate(currentStage.planned_date)}</div>
                <div style={{ fontSize: 9, color: isDelayed ? 'var(--red)' : 'var(--green)', letterSpacing: 1, fontWeight: 600 }}>TAP TO UPDATE →</div>
              </div>
            </div>

            {isDelayed && expectedStage && expectedStage.uniqueid !== currentStage.uniqueid && (
              <div
                onClick={() => {
                  setUpdating(expectedStage);
                  setActualDate(expectedStage.actual_date ? toISOforPicker(expectedStage.actual_date) : todayISOforPicker());
                  setRemark('');
                }}
                style={{
                  padding: 10, borderRadius: 6,
                  background: 'rgba(255,255,255,0.03)',
                  borderLeft: '3px solid #E3C15E',
                  cursor: 'pointer', transition: 'background 0.15s'
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.07)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
              >
                <div style={{ fontSize: 9, color: 'var(--text3)', letterSpacing: 1, marginBottom: 4 }}>EXPECTED BY NOW</div>
                <div style={{
                  fontSize: 14, color: 'var(--yellow)', fontWeight: 600,
                  wordBreak: 'break-word', lineHeight: 1.2
                }}>
                  {expectedStage.stage_name}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                  <div style={{ fontSize: 10, color: 'var(--text3)' }}>Plan: {fmtDate(expectedStage.planned_date)}</div>
                  <div style={{ fontSize: 9, color: 'var(--yellow)', letterSpacing: 1, fontWeight: 600 }}>TAP TO UPDATE →</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Shade-wise breakdown — TABLE format */}
      {shades.length > 0 && (
        <div style={{ borderBottom: '1px solid var(--border)' }}>
          <div
            onClick={() => setShowShades(s => !s)}
            style={{ padding: '10px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
          >
            <div style={{ fontSize: 11, color: 'var(--gold)', letterSpacing: 2 }}>
              SHADE BREAKDOWN ({shades.length})
            </div>
            <div style={{ color: 'var(--gold)', fontSize: 16 }}>{showShades ? '▾' : '▸'}</div>
          </div>
          {showShades && (order.order_type === 'fabric' ? (
            // ─── GSP / Fabric template — Unit | Design | Shade | Selvedge | Qty | Rate | Amount ───
            <div style={{ padding: '0 12px 14px', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, minWidth: 680, whiteSpace: 'nowrap' }}>
                <thead>
                  <tr style={{ background: 'var(--bg2)', color: 'var(--gold)', textAlign: 'left', borderBottom: '2px solid var(--gold)' }}>
                    <th style={{ padding: '9px 6px', fontWeight: 700, letterSpacing: 0.4 }}>Unit</th>
                    <th style={{ padding: '9px 6px', fontWeight: 700, letterSpacing: 0.4 }}>Design</th>
                    <th style={{ padding: '9px 6px', fontWeight: 700, letterSpacing: 0.4 }}>Shade</th>
                    <th style={{ padding: '9px 6px', fontWeight: 700, letterSpacing: 0.4 }}>Selvedge</th>
                    <th style={{ padding: '9px 6px', fontWeight: 700, letterSpacing: 0.4, textAlign: 'right' }}>Qty</th>
                    <th style={{ padding: '9px 6px', fontWeight: 700, letterSpacing: 0.4, textAlign: 'right' }}>Price</th>
                    <th style={{ padding: '9px 6px', fontWeight: 700, letterSpacing: 0.4, textAlign: 'right' }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {shades.map(s => (
                    <tr key={s.iid + '_' + (s.shade || '')} style={{ borderBottom: '1px solid var(--border)', color: 'var(--text2)' }}>
                      <td style={{ padding: '8px 6px', color: 'var(--text2)' }}>{titleCase(s.main_category) || '—'}</td>
                      <td style={{ padding: '8px 6px', color: 'var(--text2)' }}>{titleCase(s.style_name) || '—'}</td>
                      <td style={{ padding: '8px 6px', color: 'var(--text)', fontWeight: 500 }}>{titleCase(s.shade) || '—'}</td>
                      <td style={{ padding: '8px 6px', color: 'var(--text3)' }}>{titleCase(s.Fabric_desc) || '—'}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'right' }}>{(s.orderqty || 0).toLocaleString()}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'right' }}>{s.rate ? '₹' + Number(s.rate).toFixed(2) : '—'}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'right', color: 'var(--gold)' }}>₹{((s.amount || 0)/100000).toFixed(2)}L</td>
                    </tr>
                  ))}
                  {shades.length > 1 && (() => {
                    const tot = shades.reduce((a, s) => ({
                      ord: a.ord + (s.orderqty || 0),
                      amt: a.amt + (s.amount || 0),
                    }), { ord: 0, amt: 0 });
                    const avgPrice = tot.ord > 0 ? tot.amt / tot.ord : 0;
                    return (
                      <tr style={{ borderTop: '2px solid var(--border)', color: 'var(--gold)', fontWeight: 500 }}>
                        <td colSpan={4} style={{ padding: '8px 6px' }}>TOTAL</td>
                        <td style={{ padding: '8px 6px', textAlign: 'right' }}>{tot.ord.toLocaleString()}</td>
                        <td style={{ padding: '8px 6px', textAlign: 'right' }}>₹{avgPrice.toFixed(2)}</td>
                        <td style={{ padding: '8px 6px', textAlign: 'right' }}>₹{(tot.amt/100000).toFixed(2)}L</td>
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          ) : (
            // ─── TAS / Garment template ───
            <div style={{ padding: '0 12px 14px', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, minWidth: 1040, whiteSpace: 'nowrap' }}>
                <thead>
                  <tr style={{ background: 'var(--bg2)', color: 'var(--gold)', textAlign: 'left', borderBottom: '2px solid var(--gold)' }}>
                    <th style={{ padding: '9px 6px', fontWeight: 700, letterSpacing: 0.4 }}>Product</th>
                    <th style={{ padding: '9px 6px', fontWeight: 700, letterSpacing: 0.4 }}>Style</th>
                    <th style={{ padding: '9px 6px', fontWeight: 700, letterSpacing: 0.4 }}>Shade</th>
                    <th style={{ padding: '9px 6px', fontWeight: 700, letterSpacing: 0.4, textAlign: 'right' }}>Order</th>
                    <th style={{ padding: '9px 6px', fontWeight: 700, letterSpacing: 0.4, textAlign: 'right' }}>Bal</th>
                    <th style={{ padding: '9px 6px', fontWeight: 700, letterSpacing: 0.4, textAlign: 'right' }}>%</th>
                    <th style={{ padding: '9px 6px', fontWeight: 700, letterSpacing: 0.4, textAlign: 'right' }}>Rate</th>
                    <th style={{ padding: '9px 6px', fontWeight: 700, letterSpacing: 0.4, textAlign: 'right' }}>Amount</th>
                    <th style={{ padding: '9px 6px', fontWeight: 700, letterSpacing: 0.4, textAlign: 'right' }}>Cons.</th>
                    <th style={{ padding: '9px 6px', fontWeight: 700, letterSpacing: 0.4, textAlign: 'right' }}>Apx Mtrs</th>
                    <th style={{ padding: '9px 6px', fontWeight: 700, letterSpacing: 0.4 }}>Factory</th>
                    <th style={{ padding: '9px 6px', fontWeight: 700, letterSpacing: 0.4 }}>Fabric</th>
                    <th style={{ padding: '9px 6px', fontWeight: 700, letterSpacing: 0.4 }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {shades.map(s => {
                    const dispatched = s.dspqty || 0;
                    const ordered = s.orderqty || 0;
                    const bal = s.bal_qty || 0;
                    // Show 1-decimal precision and never round up to 100% while bal > 0.
                    // 5572/5600 = 99.5% (not 100%); 0/3000 = 0%; 3000/3000 = 100%.
                    const rawPct = ordered > 0 ? (dispatched / ordered) * 100 : 0;
                    let dPct;
                    if (bal <= 0 && dispatched >= ordered) dPct = '100%';
                    else if (rawPct >= 99.95)              dPct = '99.9%';   // clamp visually
                    else                                   dPct = rawPct.toFixed(1) + '%';
                    // Status — derived from dispatch unless ERP set a terminal flag
                    const erpStatus = s.Status || 'Pending';
                    const isTerminal = ['Close', 'Cancel', 'Suspend', 'Dropped', 'Hold By Client'].includes(erpStatus);
                    let dispStatus, stColor;
                    if (isTerminal) {
                      dispStatus = erpStatus;
                      stColor = erpStatus === 'Close' ? 'var(--text3)' : 'var(--red)';
                    } else if (bal <= 0 && dispatched >= ordered) {
                      dispStatus = 'Delivered';
                      stColor    = 'var(--green)';
                    } else if (dispatched > 0) {
                      dispStatus = 'Partial';
                      stColor    = 'var(--teal)';
                    } else {
                      dispStatus = 'Pending';
                      stColor    = 'var(--yellow)';
                    }
                    return (
                      <tr key={s.iid + '_' + (s.shade || '')} style={{ borderBottom: '1px solid var(--border)', color: 'var(--text2)' }}>
                        <td style={{ padding: '8px 6px', color: 'var(--text2)' }}>{s.sub_category || s.main_category || s.product || '—'}</td>
                        <td style={{ padding: '8px 6px', color: 'var(--text2)' }}>{s.style_name || '—'}</td>
                        <td style={{ padding: '8px 6px', color: 'var(--text)', fontWeight: 500 }}>{s.shade || '—'}</td>
                        <td style={{ padding: '8px 6px', textAlign: 'right' }}>{ordered.toLocaleString()}</td>
                        <td style={{ padding: '8px 6px', textAlign: 'right' }}>{(s.bal_qty || 0).toLocaleString()}</td>
                        <td style={{ padding: '8px 6px', textAlign: 'right', color: dispatched > 0 ? 'var(--green)' : 'var(--text3)' }}>{dPct}</td>
                        <td style={{ padding: '8px 6px', textAlign: 'right' }}>{s.rate ? '₹' + s.rate : '—'}</td>
                        <td style={{ padding: '8px 6px', textAlign: 'right', color: 'var(--gold)' }}>₹{((s.amount || 0)/100000).toFixed(2)}L</td>
                        <td style={{ padding: '8px 6px', textAlign: 'right', color: 'var(--text3)' }}>{s.consumption ? s.consumption.toFixed(2) : '—'}</td>
                        <td style={{ padding: '8px 6px', textAlign: 'right', color: 'var(--text3)' }}>{s.approx_mtrs ? s.approx_mtrs.toFixed(2) : '—'}</td>
                        <td style={{ padding: '8px 6px', color: 'var(--text3)' }}>{s.factory || '—'}</td>
                        <td style={{ padding: '8px 6px', color: 'var(--text3)' }}>{s.Fabric_desc || '—'}</td>
                        <td style={{ padding: '8px 6px', color: stColor, fontWeight: 600 }}>{dispStatus}</td>
                      </tr>
                    );
                  })}
                  {shades.length > 1 && (() => {
                    const tot = shades.reduce((a, s) => ({
                      ord: a.ord + (s.orderqty || 0),
                      dsp: a.dsp + (s.dspqty || 0),
                      bal: a.bal + (s.bal_qty || 0),
                      amt: a.amt + (s.amount || 0),
                      mtrs: a.mtrs + (s.approx_mtrs || 0),
                    }), { ord: 0, dsp: 0, bal: 0, amt: 0, mtrs: 0 });
                    const rawTotPct = tot.ord > 0 ? (tot.dsp / tot.ord) * 100 : 0;
                    let totPct;
                    if (tot.bal <= 0 && tot.dsp >= tot.ord) totPct = '100%';
                    else if (rawTotPct >= 99.95)            totPct = '99.9%';
                    else                                    totPct = rawTotPct.toFixed(1) + '%';
                    return (
                      <tr style={{ borderTop: '2px solid var(--border)', color: 'var(--gold)', fontWeight: 500 }}>
                        <td colSpan={3} style={{ padding: '8px 6px' }}>TOTAL</td>
                        <td style={{ padding: '8px 6px', textAlign: 'right' }}>{tot.ord.toLocaleString()}</td>
                        <td style={{ padding: '8px 6px', textAlign: 'right' }}>{tot.bal.toLocaleString()}</td>
                        <td style={{ padding: '8px 6px', textAlign: 'right' }}>{totPct}</td>
                        <td></td>
                        <td style={{ padding: '8px 6px', textAlign: 'right' }}>₹{(tot.amt/100000).toFixed(2)}L</td>
                        <td></td>
                        <td style={{ padding: '8px 6px', textAlign: 'right' }}>{tot.mtrs.toFixed(0)}</td>
                        <td colSpan={3}></td>
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {/* Critical stages — overdue key routing stages with inline actual-date entry.
          Placed directly below the SHADE BREAKDOWN (quality details) table per request.
          Shared component (also used on the TAS-status drill) so every critical TAS shows it. */}
      <CriticalStagesBanner tasNo={order.tas_no} orderType={order.order_type || 'garment'}
        onSaved={(c, val) => {
          const parsed = new Date(val + 'T00:00:00').toISOString();
          setStages(prev => prev.map(s => s.uniqueid === c.uniqueid ? { ...s, actual_date: parsed, status: 'done' } : s));
        }} />

      {/* PENDING Stages — box grid */}
      {!loading && filtered.length > 0 && (
        <div style={{ padding: '10px 20px 0', fontSize: 11, color: 'var(--gold)', letterSpacing: 2 }}>
          ⏳ PENDING STAGES ({filtered.length})
        </div>
      )}
      {loading ? (
        <div className="loading">LOADING STAGES...</div>
      ) : (() => {
        // Group pending by stage category
        const categoryOrder = ['Pre Order Routing Stage', 'Estimated Tna', 'Fabric Routing Stage', 'Garment Routing Stage'];
        const pendingGrouped = {};
        filtered.forEach(s => {
          const cat = s.stage_category || 'Other';
          if (!pendingGrouped[cat]) pendingGrouped[cat] = [];
          pendingGrouped[cat].push(s);
        });
        const pCatKeys = [...categoryOrder.filter(c => pendingGrouped[c]), ...Object.keys(pendingGrouped).filter(c => !categoryOrder.includes(c))];
        return pCatKeys.map(cat => {
          const isOpen = openCategories[cat] === true; // default collapsed — user expands as needed
          return (
          <div key={'pending_' + cat}>
            <div
              onClick={() => setOpenCategories(prev => ({ ...prev, [cat]: !isOpen }))}
              style={{ padding: '8px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(212,185,120,0.04)', borderTop: '1px solid var(--border)' }}
            >
              <div style={{ fontSize: 10, color: 'var(--gold)', letterSpacing: 2 }}>{cat.toUpperCase()} ({pendingGrouped[cat].length})</div>
              <div style={{ fontSize: 12, color: 'var(--gold)' }}>{isOpen ? '▾' : '▸'}</div>
            </div>
            {isOpen && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10, padding: '10px 16px 14px' }}>
          {pendingGrouped[cat].map(s => {
            const isDone     = s.status === 'done';
            const isOverdue  = s.status === 'overdue';
            const isDueSoon  = s.status === 'due_soon';
            const isCurrent  = currentStage && s.uniqueid === currentStage.uniqueid;
            const isExpected = expectedStage && s.uniqueid === expectedStage.uniqueid;
            // Force RED on any pending stage that should already have been done by now (sortKey <= expected)
            const isLateInChain = !isDone && expectedStage && sortKey(s) <= sortKey(expectedStage);
            const accent = isDone        ? 'var(--green)'
                         : isLateInChain ? 'var(--red)'
                         : isOverdue     ? 'var(--red)'
                         : isDueSoon     ? 'var(--yellow)'
                         : 'var(--text3)';
            const icon   = isDone ? '✓' : isLateInChain || isOverdue ? '!' : isDueSoon ? '~' : '○';
            return (
              <div
                key={s.uniqueid}
                onClick={() => {
                  setUpdating(s);
                  // Pre-fill with existing actual date when editing (YYYY-MM-DD), otherwise today
                  setActualDate(s.actual_date ? toISOforPicker(s.actual_date) : todayISOforPicker());
                  setRemark('');
                }}
                style={{
                  background: 'var(--bg2)',
                  border: '1px solid var(--border)',
                  borderLeft: `3px solid ${accent}`,
                  borderRadius: 8,
                  padding: 12,
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                  opacity: isDone ? 0.9 : 1,
                  transition: 'all 0.15s'
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg3)'; e.currentTarget.style.borderColor = accent; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg2)'; e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.borderLeftColor = accent; }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 6 }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 600 }}>{s.stage_name}</div>
                    {(isCurrent || isExpected) && (
                      <div style={{ display: 'flex', gap: 4, marginTop: 3, flexWrap: 'wrap' }}>
                        {isCurrent && (
                          <span style={{ fontSize: 8, padding: '1px 5px', borderRadius: 8, background: 'rgba(82,194,120,0.18)', color: 'var(--green)', letterSpacing: 1, fontWeight: 600 }}>
                            CURRENT
                          </span>
                        )}
                        {isExpected && !isCurrent && (
                          <span style={{ fontSize: 8, padding: '1px 5px', borderRadius: 8, background: 'rgba(227,193,94,0.18)', color: 'var(--yellow)', letterSpacing: 1, fontWeight: 600 }}>
                            EXPECTED
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div style={{
                    width: 22, height: 22, borderRadius: '50%',
                    background: `${accent}26`, color: accent,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, fontWeight: 600, flexShrink: 0
                  }}>{icon}</div>
                </div>

                <div style={{ fontSize: 10, color: 'var(--text3)', display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <div>📅 Plan: <span style={{ color: 'var(--text2)' }}>{fmtDate(s.planned_date)}</span></div>
                  <div>✓ Actual: <span style={{ color: s.actual_date ? 'var(--text2)' : 'var(--text3)' }}>{s.actual_date ? fmtDate(s.actual_date) : '—'}</span></div>
                </div>

                {s.delay_days != null && (
                  <div style={{ fontSize: 10, color: s.delay_days <= 0 ? 'var(--green)' : 'var(--red)' }}>
                    {s.delay_days <= 0 ? '✓ On time' : `+${s.delay_days}d late`}
                  </div>
                )}

                {s.remarks && <div style={{ fontSize: 10, color: 'var(--text3)', borderTop: '1px solid var(--border)', paddingTop: 4, marginTop: 2 }}>{s.remarks}</div>}

                <div style={{ fontSize: 10, color: isDone ? 'var(--text3)' : accent, marginTop: 'auto', letterSpacing: 1, textAlign: 'right' }}>
                  {isDone ? '✎ TAP TO EDIT →' : 'TAP TO UPDATE →'}
                </div>
              </div>
            );
          })}
            </div>
            )}
          </div>
          );
        });
      })()}

      {/* COMPLETED stages — collapsible + grouped by stage category */}
      {!loading && (() => {
        // Only genuinely-completed stages. Skipped / not-applicable / phantom
        // stages are never shown (not here, not in pending). Descending: the most
        // recently completed stage sits at the top, older completions below.
        const completedAndSkipped = stages
          .filter(s => s.status === 'done')
          .sort((a, b) => sortKey(b) - sortKey(a));
        if (completedAndSkipped.length === 0) return null;

        // Category order reversed for the completed list (latest workflow stage on top).
        const categoryOrder = ['Garment Routing Stage', 'Fabric Routing Stage', 'Estimated Tna', 'Pre Order Routing Stage'];
        const grouped = {};
        completedAndSkipped.forEach(s => {
          const cat = s.stage_category || 'Other';
          if (!grouped[cat]) grouped[cat] = [];
          grouped[cat].push(s);
        });
        const catKeys = [...categoryOrder.filter(c => grouped[c]), ...Object.keys(grouped).filter(c => !categoryOrder.includes(c))];

        return (
          <>
            <div
              onClick={() => setShowCompleted(v => !v)}
              style={{
                padding: '12px 20px', cursor: 'pointer', display: 'flex',
                justifyContent: 'space-between', alignItems: 'center',
                borderTop: '1px solid var(--border)', borderBottom: showCompleted ? '1px solid var(--border)' : 'none'
              }}
            >
              <div style={{ fontSize: 11, color: 'var(--green)', letterSpacing: 2 }}>
                ✓ COMPLETED STAGES ({completedAndSkipped.length})
              </div>
              <div style={{ fontSize: 14, color: 'var(--green)' }}>{showCompleted ? '▾' : '▸'}</div>
            </div>

            {showCompleted && catKeys.map(cat => {
              const catKey = 'c_' + cat;
              const isCatOpen = openCategories[catKey] === true; // default collapsed
              return (
              <div key={cat}>
                <div
                  onClick={() => setOpenCategories(prev => ({ ...prev, [catKey]: !isCatOpen }))}
                  style={{ padding: '8px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--green-bg)', borderTop: '1px solid var(--border)' }}
                >
                  <div style={{ fontSize: 10, color: 'var(--text3)', letterSpacing: 2 }}>{cat.toUpperCase()} ({grouped[cat].length})</div>
                  <div style={{ fontSize: 12, color: 'var(--green)' }}>{isCatOpen ? '▾' : '▸'}</div>
                </div>
                {isCatOpen && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10, padding: '8px 16px 16px' }}>
                  {grouped[cat].map(s => {
                const explicitlySkipped = (s.applicable === 0 || s.applicable === '0' || s.applicable === false);
                const autoSkipped = !explicitlySkipped && isAutoSkipped(s);
                const skipped = explicitlySkipped || autoSkipped;
                return (
                  <div
                    key={'done_' + s.uniqueid}
                    onClick={() => {
                      // Auto-skipped boxes ARE clickable so user can confirm/enter a real date
                      if (explicitlySkipped) return;
                      setUpdating(s);
                      setActualDate(s.actual_date ? toISOforPicker(s.actual_date) : todayISOforPicker());
                      setRemark('');
                    }}
                    style={{
                      background: skipped ? 'rgba(255,255,255,0.02)' : 'var(--green-bg)',
                      border: skipped ? '1px dashed var(--border)' : '1px solid var(--border)',
                      borderLeft: `3px solid ${skipped ? 'var(--text3)' : 'var(--green)'}`,
                      borderRadius: 8,
                      padding: 10,
                      cursor: skipped ? 'default' : 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 4,
                      opacity: skipped ? 0.55 : 0.85,
                      transition: 'all 0.15s'
                    }}
                    onMouseEnter={e => { if (!skipped) { e.currentTarget.style.background = 'var(--green-bg)'; e.currentTarget.style.borderColor = 'var(--green)'; e.currentTarget.style.opacity = 1; } }}
                    onMouseLeave={e => { if (!skipped) { e.currentTarget.style.background = 'var(--green-bg)'; e.currentTarget.style.borderColor = '#DDD2B8'; e.currentTarget.style.borderLeftColor = 'var(--green)'; e.currentTarget.style.opacity = 0.85; } }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
                      <div style={{
                        fontSize: 12, fontWeight: 600, minWidth: 0, flex: 1,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        color: skipped ? 'var(--text3)' : 'var(--text2)',
                        textDecoration: skipped ? 'line-through' : 'none'
                      }}>{s.stage_name}</div>
                      <div style={{
                        width: 20, height: 20, borderRadius: '50%',
                        background: explicitlySkipped ? 'rgba(136,136,136,0.15)' : autoSkipped ? 'rgba(91,194,199,0.18)' : 'rgba(82,194,120,0.2)',
                        color: explicitlySkipped ? 'var(--text3)' : autoSkipped ? 'var(--teal)' : 'var(--green)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 600, flexShrink: 0
                      }}>{explicitlySkipped ? '⏭' : autoSkipped ? '↷' : '✓'}</div>
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--text3)', display: 'flex', justifyContent: 'space-between' }}>
                      <span>Plan: <span style={{ color: 'var(--text3)' }}>{fmtDate(s.planned_date)}</span></span>
                      <span style={{ color: explicitlySkipped ? 'var(--text3)' : autoSkipped ? 'var(--teal)' : 'var(--green)' }}>
                        {explicitlySkipped
                          ? 'Not applicable'
                          : autoSkipped
                            ? `Auto · later stage done`
                            : `Done: ${fmtDate(s.actual_date)}`}
                      </span>
                    </div>
                    {!skipped && s.delay_days != null && (
                      <div style={{ fontSize: 9, color: s.delay_days <= 0 ? 'var(--green)' : 'var(--red)', textAlign: 'right' }}>
                        {s.delay_days <= 0 ? '✓ on time' : `+${s.delay_days}d late`}
                      </div>
                    )}
                    {(!skipped || autoSkipped) && (
                      <div style={{ fontSize: 9, color: autoSkipped ? 'var(--teal)' : 'var(--text3)', textAlign: 'right', letterSpacing: 1 }}>
                        {autoSkipped ? '✎ ENTER ACTUAL DATE →' : '✎ TAP TO EDIT →'}
                      </div>
                    )}
                  </div>
                );
              })}
                </div>
                )}
              </div>
              );
            })}
          </>
        );
      })()}

      {/* Update Modal */}
      {updating && (
        <div className="modal-overlay" onClick={e => { if (e.target.className === 'modal-overlay') setUpdating(null); }}>
          <div className="modal">
            <div className="modal-title">{updating.status === 'done' ? '✎ Edit Actual Date' : 'Update Stage'}</div>
            <div className="modal-sub">{updating.stage_name}</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 6 }}>
              Planned: {fmtDate(updating.planned_date)}
            </div>
            {updating.status === 'done' && updating.actual_date && (
              <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12, padding: '6px 10px', background: 'var(--green-bg)', borderRadius: 4, border: '1px solid rgba(82,194,120,0.2)' }}>
                Currently saved: <span style={{ color: 'var(--green)' }}>{fmtDate(updating.actual_date)}</span> — edit below to correct
              </div>
            )}
            {/* Native date picker — same style as the CUSTOM filter, defaults to today */}
            <input
              type="date"
              value={actualDate}
              onChange={e => setActualDate(e.target.value)}
              max={todayISOforPicker()}
              style={{
                width: '100%', boxSizing: 'border-box', padding: '12px 14px',
                background: 'var(--border)', border: '1px solid var(--border)', borderRadius: 6,
                color: 'var(--text)', fontSize: 14, outline: 'none', marginBottom: 12,
                colorScheme: 'dark'
              }}
              onFocus={e => e.target.style.borderColor = 'var(--gold)'}
              onBlur={e => e.target.style.borderColor = 'var(--border)'}
            />
            <textarea
              placeholder={updating.status === 'done' ? 'Reason for edit (optional)' : 'Add remark (optional) +3 XP bonus'}
              value={remark}
              onChange={e => setRemark(e.target.value)}
            />
            <button className="btn-primary" onClick={handleUpdate} disabled={saving}>
              {saving ? 'Saving...' : updating.status === 'done' ? '✎ UPDATE DATE' : '✓ MARK DONE'}
            </button>
            {updating.status !== 'done' && (
              <button
                onClick={handleSkip}
                disabled={saving}
                style={{
                  width: '100%', marginTop: 8, padding: '12px',
                  background: 'transparent', color: 'var(--yellow)',
                  border: '1px solid #E3C15E', borderRadius: 6,
                  cursor: 'pointer', fontSize: 13, letterSpacing: 1
                }}
              >
                ⏭ SKIP STAGE (not applicable)
              </button>
            )}
            <button className="btn-cancel" onClick={() => setUpdating(null)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
